/**
 * A `MouthSource` turns whatever input we have (webcam face, or a mouse for
 * `?mouse=1` debugging) into the single intent the sim consumes: a
 * `MouthSample` in field space. This is the one seam the Phase-2 network port
 * plugs into — the loop asks for a sample and forwards it, nothing more.
 */

import type { MouthSample } from "./types.js";
import type { FaceTracker, MouthFSM, Signal } from "./face.js";

export interface MouthSource {
  update(nowMs: number): void;
  sample(): MouthSample | null;
  faceVisible: boolean;
}

type FieldPoint = { x: number; y: number };

export class FaceMouthSource implements MouthSource {
  constructor(
    private readonly tracker: FaceTracker,
    private readonly fsm: MouthFSM,
    private readonly signal: Signal,
    private readonly mapVideoNormToField: (x: number, y: number) => FieldPoint,
  ) {}

  get faceVisible(): boolean {
    return this.tracker.hasFace;
  }

  update(nowMs: number): void {
    this.tracker.update(nowMs);
    if (!this.tracker.hasFace) return;
    const value = this.signal === "jaw" ? this.tracker.jawOpen : this.tracker.lipGapRatio();
    if (value !== null) this.fsm.update(value, nowMs);
  }

  sample(): MouthSample | null {
    const e = this.tracker.mouthEllipseVideoNorm();
    if (!e) return null;
    // Map the center and a corner offset by (rx, ry): the radii in field space
    // are the deltas, so we inherit the mapper's mirror + cover-fit scaling.
    const center = this.mapVideoNormToField(e.cx, e.cy);
    const edge = this.mapVideoNormToField(e.cx + e.rx, e.cy + e.ry);
    return {
      cx: center.x,
      cy: center.y,
      rx: Math.abs(edge.x - center.x),
      ry: Math.abs(edge.y - center.y),
      open: this.fsm.open,
      closeSeq: this.fsm.closeSeq,
    };
  }
}

/**
 * Debug source for `?mouse=1`: no webcam. The mouth ellipse follows the pointer
 * (normalized to the game container); press = open, release = a bite.
 */
export class MouseMouthSource implements MouthSource {
  faceVisible = true;

  private nx = 0.5;
  private ny = 0.5;
  private isOpen = false;
  private closeSeq = 0;

  constructor(private readonly el: HTMLElement) {
    el.addEventListener("pointermove", (e) => this.onMove(e));
    el.addEventListener("pointerdown", (e) => {
      this.onMove(e);
      this.isOpen = true;
    });
    el.addEventListener("pointerup", () => this.onUp());
    el.addEventListener("pointercancel", () => this.onUp());
  }

  private onMove(e: PointerEvent): void {
    const r = this.el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    this.nx = clamp01((e.clientX - r.left) / r.width);
    this.ny = clamp01((e.clientY - r.top) / r.height);
  }

  private onUp(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.closeSeq++;
    }
  }

  update(_nowMs: number): void {
    // Pointer events drive state directly; nothing to poll.
  }

  sample(): MouthSample | null {
    return { cx: this.nx, cy: this.ny, rx: 0.07, ry: 0.05, open: this.isOpen, closeSeq: this.closeSeq };
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
